<template>
  <div class="landing">
    <BlogNav />

    <!-- HERO -->
    <section class="hero">
      <div class="hero-inner">
        <h1 class="hero-title">
          {{ $t('landing.about.hero.titleLine1') }}<br />
          <span class="font-bold">{{ $t('landing.about.hero.titleLine2') }}</span>
        </h1>
        <p class="hero-sub">
          {{ $t('landing.about.hero.subtitle') }}
          <strong>{{ $t('landing.about.hero.subtitleStrong') }}</strong>
        </p>

        <a
          class="btn btn-primary rounded-pill mb-24"
          href="https://calendly.com/d/cytz-wbs-3xn/demo-query-juriste-ia-recherche-juridique"
          @click.prevent="openCalendlyDemo"
          target="_blank"
          rel="noopener"
          style = "background-color: #625CFB; font-weight: 100; margin-top: 1em;"
        >
          {{ $t('landing.about.hero.ctaDemo') }}
        </a>

        <img
          src="@/assets/hero.png"
          class="hero-img rounded-xl shadow-lg"
          :alt="$t('landing.about.hero.imageAlt')"
        />
      </div>
    </section>

    <!-- TRUST -->
    <section class="trust">
      <h3 class="trust-title">{{ $t('landing.about.trust.title') }}</h3>

      <div class="logo-row">
        <div
          v-for="(company, index) in companies"
          :key="'logo-'+index"
          class="logo-item"
        >
          <img
            :src="require(`@/assets/logos-group/${company}`)"
            :alt="`${$t('landing.about.trust.logoAltPrefix')} ${index + 1}`"
            class="company-logo"
          />
          <span class="divider" aria-hidden="true">|</span>
        </div>
      </div>

      <p class="trust-note">
        {{ $t('landing.about.trust.noteSmall') }}
      </p>

      <button class="btn btn-outline rounded-pill" @click="openCalendlyDemo">
        {{ $t('landing.about.trust.cta') }}
      </button>
    </section>

    <!-- SECTION 3 -->
    <section class="feature-two-col">
      <div class="col text">
        <a href="#" class="link-info">{{ $t('landing.about.feature.linkInfo') }}</a>
        <h2 class="section-heading">{{ $t('landing.about.feature.heading') }}</h2>
        <p class="section-paragraph">
          {{ $t('landing.about.feature.paragraph') }}
        </p>

        <ul class="checklist">
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            {{ $t('landing.about.feature.check1') }}
          </li>
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            {{ $t('landing.about.feature.check2') }}
          </li>
          <li>
            <img class="check" src="@/assets/check.svg" alt="" />
            {{ $t('landing.about.feature.check3') }}
          </li>
        </ul>
      </div>

      <div class="col visual">
        <img
          src="@/assets/que.png"
          class="main-visual rounded-xl shadow-lg"
          :alt="$t('landing.about.feature.imageAlt')"
        />
      </div>
    </section>

    <!-- SECTION 4 -->
    <section class="cards-section">
      <h2 class="big-heading">{{ $t('landing.about.cards.heading') }}</h2>

      <div class="card-grid">
        <article
          v-for="(card, index) in section4"
          :key="'card-'+index"
          class="info-card"
        >
          <img
            class="card-hero rounded-xl"
            :src="require(`@/assets/section4/${card.img}.png`)"
            :alt="`${$t('landing.about.cards.illustrationAltPrefix')} ${index + 1}`"
          />
          <h5 class="card-title">
            <img
              class="card-icon"
              :src="require(`@/assets/section4/${card.icon}.svg`)"
              alt=""
            />
            {{ $t(card.titleKey) }}
          </h5>
          <p class="card-text">{{ $t(card.textKey) }}</p>
        </article>
      </div>
    </section>

    <!-- CTA BAND -->
    <section class="cta-band">
      <div class="cta-inner">
        <div class="cta-copy">
          <h3 class="cta-title">{{ $t('landing.about.cta.title') }}</h3>
          <p class="cta-text">
            {{ $t('landing.about.cta.text') }}
          </p>
        </div>

        <div class="cta-form">
          <button class="btn btn-ghost rounded-pill" @click="openCalendlyDemo">
            {{ $t('landing.about.cta.button') }}
          </button>
          <p class="cta-foot">{{ $t('landing.about.cta.foot') }}</p>
        </div>
      </div>
    </section>

    <!-- SECTION 5 -->
    <section class="testimonials">
      <div class="wrap">
        <a href="#" class="link-info">{{ $t('landing.about.testimonials.linkInfo') }}</a>
        <h2 class="section-heading spaced">
          {{ $t('landing.about.testimonials.headingPart1') }} <br />
          <span class="text-info">{{ $t('landing.about.testimonials.headingPart2') }}</span>
        </h2>

        <div class="testimonial-grid">
          <article
            v-for="(item, index) in section5"
            :key="'t-'+index"
            class="testimonial-card rounded-xl shadow-sm"
          >
            <div class="t-body">
              <h5 class="t-title">{{ item.title }}</h5>
              <p class="t-text">{{ item.text }}</p>
            </div>
            <div class="t-footer">
              <span class="profile">
                <img
                  :src="require(`@/assets/section5/${item.avatar}.svg`)"
                  class="avatar"
                  alt=""
                />
                <span>
                  <h6 class="name">{{ item.name }}</h6>
                  <a href="#" class="handle">{{ item.username }}</a>
                </span>
              </span>
              <img
                :src="require(`@/assets/section5/${item.icon}.svg`)"
                class="social"
                alt=""
              />
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- SECTION 6 -->
    <section class="blog-list">
      <div class="wrap">
        <a class="link-info" href="#">{{ $t('landing.about.blog.linkInfo') }}</a>
        <h2 class="section-heading">{{ $t('landing.about.blog.heading') }}</h2>

        <div class="blog-grid">
          <article
            v-for="(item, index) in blogs"
            :key="'b-'+index"
            class="blog-card"
          >
            <img
              class="blog-img"
              src="@/assets/dummy-blog.png"
              :alt="$t('landing.about.blog.imageAlt')"
            />
            <div class="blog-body">
              <div class="chips">
                <span
                  v-for="(chip, i) in item.chips"
                  :key="'chip-'+index+'-'+i"
                  class="chip"
                >
                  {{ chip }}
                </span>
              </div>
              <h3 class="blog-title">{{ $t('landing.about.blog.placeholderTitle') }}</h3>
              <p class="blog-text">
                {{ $t('landing.about.blog.placeholderText') }}
              </p>
              <a
                class="btn btn-outline rounded-pill small"
                href="https://www.query-juriste.com/blog"
                target="_blank"
                rel="noopener"
              >
                {{ $t('landing.about.blog.readMore') }}
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>
        <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-logo-section">
          <img src="/logo-query-juriste-png.png" :alt="$t('landing.about.footer.logoAlt')" class="logo" />
          <p class="footer-description">
            {{ $t('landing.about.footer.description') }}
          </p>
        </div>

        <div class="footer-links-grid">
          <div class="footer-column">
            <h4>{{ $t('landing.about.footer.columnPages') }}</h4>
            <ul>
              <li><a href="/">{{ $t('landing.about.footer.linkHome') }}</a></li>
              <li><a href="https://www.query-juriste.com/blog">{{ $t('landing.about.footer.linkBlog') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>{{ $t('landing.about.footer.columnProduct') }}</h4>
            <ul>
              <li><a href="/loginPage">{{ $t('landing.about.footer.linkLogin') }}</a></li>
              <li><a href="/contactPage">{{ $t('landing.about.footer.linkContact') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>{{ $t('landing.about.footer.columnCompany') }}</h4>
            <ul>
              <li><a href="/mentionsPage">{{ $t('landing.about.footer.linkMentions') }}</a></li>
              <li><a href="/conditions-utilisation">{{ $t('landing.about.footer.linkCgu') }}</a></li>
              <li><a href="/conditions-vente">{{ $t('landing.about.footer.linkCgv') }}</a></li>
              <li><a href="/politique-donnees">{{ $t('landing.about.footer.linkPrivacy') }}</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>{{ $t('landing.about.footer.copyright') }}</p>
      </div>
    </footer>

  </div>
</template>

<script>
import BlogNav from "@/components/BlogNav.vue";
import { openCalendlyDemo } from "@/utils/calendly";

export default {
  name: "RootPage",
  components: { BlogNav },
  methods: {
    openCalendlyDemo,
  },
  data() {
    return {
      companies: [
        "peng.png",
        "amplitude.png",
        "verox.png",
        "republica.png",
        "amplitude.png",
        "invoice.png",
      ],
      section4: [
        {
          img: "card1",
          icon: "icon1",
          titleKey: "landing.about.cards.card1Title",
          textKey: "landing.about.cards.card1Text",
        },
        {
          img: "card2",
          icon: "icon2",
          titleKey: "landing.about.cards.card2Title",
          textKey: "landing.about.cards.card2Text",
        },
        {
          img: "card3",
          icon: "icon3",
          titleKey: "landing.about.cards.card3Title",
          textKey: "landing.about.cards.card3Text",
        },
      ],
      section5: [
        {
          title: "Incredibly useful product",
          text:
            "User-friendly dashboards have simplified our digital strategy management.",
          avatar: "avatar1",
          name: "Fig Nelson",
          username: "@fignel_sooon",
          icon: "twitter",
        },
        {
          title: "Incredibly useful product",
          text:
            "Has truly transformed our online presence. With its powerful analytics and seamless integration, we've gained invaluable insights.",
          avatar: "avatar2",
          name: "Sadie Berlin",
          username: "@sadiieberlin00",
          icon: "instagram",
        },
        {
          title: "Incredibly useful product",
          text:
            "We've gained invaluable insights and   improved our SEO ranking, resulting in significant business growth",
          avatar: "avatar3",
          name: "Amaya Locosta",
          username: "@amaylocosta",
          icon: "facebook",
        },
        {
          title: "Incredibly useful product",
          text:
            "We've gained invaluable insights and   improved our SEO ranking, resulting in significant business growth",
          avatar: "avatar2",
          name: "Sadie Berlin",
          username: "@sadiieberlin00",
          icon: "instagram",
        },
        {
          title: "Incredibly useful product",
          text:
            "User-friendly dashboards have simplified our digital strategy management.",
          avatar: "avatar1",
          name: "Fig Nelson",
          username: "@fignel_sooon",
          icon: "twitter",
        },
        {
          title: "Incredibly useful product",
          text:
            "User-friendly dashboards havesimplified our digital strategy management. ",
          avatar: "avatar2",
          name: "Sadie Berlin",
          username: "@sadiieberlin00",
          icon: "instagram",
        },
      ],
      blogs: [
        {
          chips: ["State", "Contrux"],
          title: "Lorem Ipsum Lorem IpsumLorem Ipsum",
          text:
            "Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum",
        },
        {
          chips: ["State", "Contrux"],
          title: "Lorem Ipsum Lorem IpsumLorem Ipsum",
          text:
            "Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum",
        },
        {
          chips: ["State", "Contrux"],
          title: "Lorem Ipsum Lorem IpsumLorem Ipsum",
          text:
            "Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum Lorem Ipdsum",
        },
      ],
    };
  },
  metaInfo() {
    return {
      title: this.$t('landing.about.metaTitle'),
      meta: [
        {
          name: "description",
          content: this.$t('landing.about.metaDescription'),
        },
      ],
      link: [{ rel: "canonical", href: "https://www.query-juriste.com/" }],
    };
  },
};
</script>

<style scoped>
/* ---------- Tokens ---------- */
:root {
  --bg: #ffffff;
  --text: #1a1a1a;
  --muted: #5e6470;
  --primary: #7e5cf2;
  --primary-600: #625CFB;
  --info: #625CFB;
  --surface: #f7f8fa;
  --border: #e6e8ec;
  --shadow: 0 10px 30px rgba(0,0,0,.12);
  --shadow-sm: 0 4px 16px rgba(0,0,0,.08);
  --radius: 14px;
}

/* ---------- Utilities ---------- */
.text-primary { color: #625CFB; }
.text-info { color: #625CFB; }
.font-bold { font-weight: 700; color: #625CFB}
.rounded-xl { border-radius: 20px; }
.rounded-pill { border-radius: 999px; }
.shadow-lg { box-shadow: var(--shadow); }
.shadow-sm { box-shadow: var(--shadow-sm); }
.mb-24 { margin-bottom: 24px; }

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 22px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform .06s ease, background .2s ease, color .2s ease, border-color .2s ease;
  will-change: transform;
}
.btn:active { transform: translateY(1px); }
.btn.small { height: 42px; padding: 0 18px; font-weight: 500; }

.btn-primary {
  background: var(--primary);
  color: #fff;
}
.btn-primary:hover { background: var(--primary-600); }

.btn-outline {
  background: transparent;
  border-color: var(--border);
  color: var(--text);
}
.btn-outline:hover {
  border-color: var(--text);
}

.btn-ghost {
  background: transparent;
  border-color: #fff;
  color: #fff;
}
.btn-ghost:hover {
  background: rgba(255,255,255,.08);
  border-color: #fff;
}

/* ---------- Layout wrappers ---------- */
.landing {
  font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif; /* ← swapped in */
  background: var(--bg);
}

.wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ---------- HERO ---------- */
.hero {
  padding: 110px 20px 60px;
}
.hero-inner {
  max-width: 1000px;
  margin: 0 auto;
  text-align: center;
}
.hero-title {
  font-size: 2.5em;
  line-height: 1.1;
  margin-bottom: 1.2em;
}
.hero-sub {
  margin: 10px 0 24px;
  color: black;
  font-size: clamp(16px, 2.6vw, 20px);
}
.hero-img {
  display: block;
  width: min(980px, 92vw);
  margin: 0 auto;
  height: auto;
}

/* ---------- TRUST ---------- */
.trust {
  text-align: center;
  padding: 30px 20px 10px;
  margin-top: 1.5em;
  margin-bottom: 1.5em;
}
.trust-title {
  margin: 0 0 22px;
  font-size: clamp(18px, 2.6vw, 26px);
}
.logo-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(14px, 3vw, 36px);
  flex-wrap: wrap;
}
.logo-item {
  display: inline-flex;
  align-items: center;
  gap: 14px;
}
.company-logo {
  max-height: 40px;
  width: auto;
  opacity: .85;
  filter: grayscale(100%);
}
.company-logo:hover { opacity: 1; filter: none; }
.divider { color: #c9cdd3; }
.trust-note {
  margin: 26px auto 18px;
  color: #000000;
  max-width: 800px;
}

/* ---------- TWO-COL FEATURE ---------- */
.feature-two-col {
  display: grid;
  grid-template-columns: 1.1fr .9fr;
  gap: 48px;
  align-items: center;
  max-width: 1200px;
  margin: 70px auto;
  padding: 0 20px;
}
.feature-two-col .col.text { min-width: 0; }
.link-info {
  color: #625CFB;
  text-decoration: none;
  font-weight: 500;
}
.link-info:hover { text-decoration: underline; }
.section-heading {
  margin: 10px 0 14px;
  font-size: clamp(26px, 4vw, 44px);
  line-height: 1.2;
}
.section-paragraph {
  color: #000000;
  margin-bottom: 16px;
}
.checklist {
  list-style: none;
  margin: 0;
  padding: 0;
  font-weight: 600;
}
.checklist li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
}
.checklist .check { width: 20px; height: 20px; }

.feature-two-col .col.visual {
  position: relative;
  min-height: 320px;
}
.bg-deco {
  position: absolute;
  left: 0;
  top: -60px;
  width: 62%;
  max-width: 520px;
  z-index: 0;
  opacity: .9;
}
.main-visual {
  position: relative;
  display: block;
  width: min(560px, 90%);
  margin-left: auto;
  z-index: 1;
}

/* ---------- CARDS SECTION ---------- */
.cards-section {
  background: url("@/assets/section4-bg.png") no-repeat center / cover;
  padding: 70px 20px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 1em;
}
.big-heading {
  max-width: 1200px;
  margin: 10px auto 26px;
  font-size: clamp(30px, 5vw, 58px);
  line-height: 1.2;
}
.card-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}
.info-card {
  border-radius: var(--radius);
  padding: 16px;
}
.card-hero { width: 100%; height: auto; margin-bottom: 12px; }
.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 8px;
  font-size: 18px;
}
.card-icon { width: 22px; height: 22px; }
.card-text { color: #000000; }

/* ---------- CTA BAND ---------- */
.cta-band {
  background: #625CFB;
  color: #fff;
  margin: 0;
  padding: 50px 20px;
}
.cta-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  align-items: center;
}
.cta-title {
  font-weight: 500;
  font-size: clamp(22px, 3.6vw, 32px);
  margin: 0 0 8px;
}
.cta-text { margin: 0; opacity: .95; }
.cta-foot { margin-top: 10px; opacity: .95; }

/* ---------- TESTIMONIALS ---------- */
.testimonials {
  background: url("@/assets/section5-bg.png") no-repeat center / cover;
  padding: 70px 20px;
}
.testimonials .wrap { max-width: 1200px; }
.section-heading.spaced { margin-top: 8px; }
.testimonial-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}
.testimonial-card {
  background: #fff;
  border: 1px solid var(--border);
  padding: 18px 14px;
}
.t-body .t-title { margin: 0 0 8px; }
.t-body .t-text { color: #000000; margin: 0 0 16px; }
.t-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.profile {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.avatar { width: 36px; height: 36px; }
.name { margin: 0 0 2px; font-size: 14px; }
.handle { color: #625CFB; text-decoration: none; font-size: 13px; }
.handle:hover { text-decoration: underline; }
.social { width: 18px; height: 18px; }

/* ---------- BLOG LIST ---------- */
.blog-list {
  padding: 70px 20px 120px;
}
.blog-list .wrap { max-width: 1200px; }
.blog-grid {
  margin-top: 26px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 26px;
}
.blog-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.blog-img {
  width: 100%;
  height: auto;
  display: block;
}
.blog-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.chips { display: flex; gap: 8px; flex-wrap: wrap; }
.chip {
  background: var(--surface);
  color: #393939;
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 500;
}
.blog-title { margin: 0; font-size: 20px; }
.blog-text { color: #000000; margin: 0 0 6px; }

/* ---------- Responsive ---------- */
@media (max-width: 1100px) {
  .feature-two-col,
  .cta-inner {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .main-visual { margin: 0 auto; }
  .bg-deco { position: absolute; left: 50%; transform: translateX(-50%); width: 80%; }
}

@media (max-width: 900px) {
  .card-grid,
  .testimonial-grid,
  .blog-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .hero { padding-top: 80px; }
  .card-grid,
  .testimonial-grid,
  .blog-grid {
    grid-template-columns: 1fr;
  }
  .logo-row { gap: 18px; }
  .divider { display: none; }
}

/* Footer (same as press page) */
.footer {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 3rem 0 0 0;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  display: flex;
  justify-content: space-between;
  gap: 4rem;
  align-items: start;
}

.footer-logo-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.footer-logo-section .logo {
  height: 60px;
  width: 60px;
  align-items: center;
  margin-bottom: 0.5rem;
}

.footer-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
  max-width: 280px;
}

.footer-links-grid {
  display: flex;
  gap: 3rem;
}

.footer-column h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer-column li {
  margin: 0;
}

.footer-column a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
  line-height: 1.4;
}

.footer-column a:hover {
  color: #625CFB;
}

.footer-bottom {
  margin-top: 2rem;
  padding: 1.5rem 0;
  border-top: 1px solid #e5e7eb;
  background-color: #f8fafc;
}

.footer-bottom p {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 3rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

/* Footer responsive */
@media (max-width: 1024px) {
  .footer-links-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .footer-content {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0 1rem;
    text-align: center;
  }

  .footer-logo-section {
    align-items: center;
  }

  .footer-description {
    text-align: center;
    max-width: none;
  }

  .footer-links-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  .footer-column {
    text-align: left;
  }

  .footer-bottom p {
    padding: 0 1rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .footer-links-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .footer-column {
    text-align: center;
  }
}

</style>
